import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Stack,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as TemplateIcon
} from '@mui/icons-material';
import api from '../../services/api';

const TemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [dialog, setDialog] = useState({
    open: false,
    mode: 'create', // 'create' or 'edit'
    template: null
  });
  const [formData, setFormData] = useState({
    name: '',
    isDefault: false,
    sections: [],
    visibility: 'private'
  });
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Error loading templates:', error);
      showAlert('Failed to load templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity }), 3000);
  };

  const openCreateDialog = () => {
    setFormData({
      name: '',
      isDefault: false,
      sections: getDefaultSections(),
      visibility: 'private'
    });
    setDialog({ open: true, mode: 'create', template: null });
  };

  const openEditDialog = (template) => {
    setFormData({
      name: template.name,
      isDefault: template.isDefault,
      sections: template.sections,
      visibility: template.visibility || 'public'
    });
    setDialog({ open: true, mode: 'edit', template });
  };

  const closeDialog = () => {
    setDialog({ open: false, mode: 'create', template: null });
    setFormData({ name: '', isDefault: false, sections: [], visibility: 'private' });
  };

  const getDefaultSections = () => [
    {
      title: 'Intro/Consent',
      sectionType: 'IC',
      order: 0,
      isCustom: false,
      items: []
    },
    {
      title: 'Presenting Complaint',
      sectionType: 'PC',
      order: 1,
      isCustom: false,
      items: []
    },
    {
      title: 'History of PC',
      sectionType: 'HPC',
      order: 2,
      isCustom: false,
      items: []
    },
    {
      title: 'Past Medical History',
      sectionType: 'PMH',
      order: 3,
      isCustom: false,
      items: []
    },
    {
      title: 'Family History',
      sectionType: 'FH',
      order: 4,
      isCustom: false,
      items: []
    },
    {
      title: 'Drug History',
      sectionType: 'DH',
      order: 5,
      isCustom: false,
      items: []
    },
    {
      title: 'Social History',
      sectionType: 'SH',
      order: 6,
      isCustom: false,
      items: []
    },
    {
      title: 'Systems Review',
      sectionType: 'SR',
      order: 7,
      isCustom: false,
      items: []
    },
    {
      title: 'Ideas, Concerns, Expectations',
      sectionType: 'ICE',
      order: 8,
      isCustom: false,
      items: []
    }
  ];

  const handleSave = async () => {
    try {
      if (!formData.name.trim()) {
        showAlert('Template name is required', 'error');
        return;
      }

      const templateData = {
        name: formData.name.trim(),
        isDefault: formData.isDefault,
        sections: formData.sections,
        visibility: formData.isDefault ? 'public' : formData.visibility
      };

      if (dialog.mode === 'create') {
        await api.post('/platform/templates', templateData);
        showAlert('Template created successfully');
      } else {
        await api.put(`/platform/templates/${dialog.template._id}`, templateData);
        showAlert('Template updated successfully');
      }

      await loadTemplates();
      closeDialog();
    } catch (error) {
      console.error('Error saving template:', error);
      showAlert(`Failed to ${dialog.mode} template`, 'error');
    }
  };

  const handleDelete = async (template) => {
    if (!template.permissions?.canDelete) {
      showAlert('You do not have permission to delete this template', 'error');
      return;
    }

    if (template.isDefault) {
      showAlert('Cannot delete the default template', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/platform/templates/${template._id}`);
      showAlert('Template deleted successfully');
      await loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      showAlert('Failed to delete template', 'error');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleVisibilityFilterChange = (event) => {
    setVisibilityFilter(event.target.value);
  };

  const filteredTemplates = templates.filter((template) => {
    if (visibilityFilter === 'all') {
      return true;
    }
    return template.visibility === visibilityFilter;
  });

  const handleDefaultToggle = (checked) => {
    setFormData((prev) => ({
      ...prev,
      isDefault: checked,
      visibility: checked ? 'public' : prev.visibility,
    }));
  };

  const handleVisibilitySelection = (value) => {
    setFormData((prev) => ({
      ...prev,
      visibility: prev.isDefault ? 'public' : value,
    }));
  };

  if (loading) {
    return <Typography>Loading templates...</Typography>;
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        mb={3}
      >
        <Typography variant="h6">
          Case Templates
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="template-visibility-filter-label">Visibility</InputLabel>
            <Select
              labelId="template-visibility-filter-label"
              value={visibilityFilter}
              label="Visibility"
              onChange={handleVisibilityFilterChange}
            >
              <MenuItem value="all">All templates</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Create Template
          </Button>
        </Stack>
      </Box>

      {alert.show && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Visibility</TableCell>
              <TableCell>Sections</TableCell>
              <TableCell>Default</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    {templates.length === 0
                      ? 'No templates available. Create one to get started.'
                      : 'No templates match the selected visibility.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTemplates.map((template) => {
                const canEdit = Boolean(template.permissions?.canEdit);
                const canDelete = Boolean(template.permissions?.canDelete);
                const ownerName = template.owner?.firstName
                  ? `${template.owner.firstName} ${template.owner.lastName || ''}`.trim()
                  : (template.createdBy?.firstName
                    ? `${template.createdBy.firstName} ${template.createdBy.lastName || ''}`.trim()
                    : '—');

                return (
                  <TableRow key={template._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <TemplateIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        {template.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={template.visibility === 'private' ? 'Private' : 'Public'}
                          color={template.visibility === 'private' ? 'secondary' : 'default'}
                          variant={template.visibility === 'private' ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {template.sections.length} sections
                      </Typography>
                    </TableCell>
                  <TableCell>
                    {template.isDefault && (
                      <Chip label="Default" color="primary" size="small" />
                    )}
                  </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(template.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          {ownerName}
                        </Typography>
                        {template.permissions?.isOwner && (
                          <Chip label="You" color="info" size="small" variant="outlined" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title={canEdit ? 'Edit template' : 'You cannot edit this template'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(template)}
                              disabled={!canEdit}
                              aria-label="Edit template"
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={canDelete ? (template.isDefault ? 'Cannot delete default template' : 'Delete template') : 'You cannot delete this template'}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(template)}
                              color="error"
                              disabled={!canDelete || template.isDefault}
                              aria-label="Delete template"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create' ? 'Create Template' : 'Edit Template'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />

          <FormControl fullWidth margin="normal" size="small" disabled={dialog.mode === 'edit' && !dialog.template?.permissions?.canToggleVisibility}>
            <InputLabel id="template-visibility-select-label">Visibility</InputLabel>
            <Select
              labelId="template-visibility-select-label"
              value={formData.isDefault ? 'public' : formData.visibility}
              label="Visibility"
              onChange={(e) => handleVisibilitySelection(e.target.value)}
            >
              <MenuItem value="public">Public — shared within your team</MenuItem>
              <MenuItem value="private" disabled={formData.isDefault}>Private — only visible to you</MenuItem>
            </Select>
            {dialog.mode === 'edit' && !dialog.template?.permissions?.canToggleVisibility && (
              <FormHelperText>Only the owner can change visibility.</FormHelperText>
            )}
            {formData.isDefault && (
              <FormHelperText>Default templates must remain public.</FormHelperText>
            )}
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isDefault}
                onChange={(e) => handleDefaultToggle(e.target.checked)}
              />
            }
            label="Set as default template"
            sx={{ mt: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This template will contain {formData.sections.length} standard case sections.
            Setting as default will make this template load automatically for new cases.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {dialog.mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManager;